'use client';

import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useQuery } from '@apollo/client';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useCart } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { GET_PRODUCTS, GET_PRODUCT_CATEGORIES } from '@/lib/graphql-queries';
import { transformGraphQLProducts } from '@/utils/transformGraphQLProduct';

const FALLBACK_IMAGE = '/assets/images/kym-products/kym-jar-07.jpg';

type UIProduct = {
  id: string;
  slug: string;
  name: string;
  price: number;
  rating: number;
  reviews: number;
  category: string;
  categorySlug: string;
  badge: string | null;
  image: string;
  alt: string;
  href: string;
  onSale: boolean;
};

function parsePrice(raw: string | number | null | undefined): number {
  if (raw === null || raw === undefined) return 0;
  if (typeof raw === 'number') return raw;
  const match = String(raw).match(/[\d.,]+/);
  if (!match) return 0;
  return parseFloat(match[0].replace(/,/g, '')) || 0;
}

// Defensive field extraction — transform output isn't consistent about
// flattening {sourceUrl} → {src} or {productCategories.nodes} → {categories}.
// Mirror beauty-store's ProductCard.getImageSrc pattern: check both shapes
// before falling back. This was the week-long bug in beauty-store, don't
// reinvent it (or fail the same way).
function getImageSrc(product: any, fallback: string): string {
  const img0 = product?.images?.[0];
  if (img0?.src) return img0.src;
  if (img0?.sourceUrl) return img0.sourceUrl;
  if (product?.image?.sourceUrl) return product.image.sourceUrl;
  if (product?.image?.src) return product.image.src;
  return fallback;
}

function getImageAlt(product: any): string {
  const img0 = product?.images?.[0];
  return img0?.alt || img0?.altText || product?.image?.altText || product?.image?.alt || product?.name || 'SweetSkyn product';
}

function getCategoryFromProduct(product: any): { name: string; slug: string } {
  const cat = product?.categories?.[0];
  if (cat?.name) return { name: cat.name, slug: cat.slug || '' };
  const rawCat = product?.productCategories?.nodes?.[0];
  if (rawCat?.name) return { name: rawCat.name, slug: rawCat.slug || '' };
  return { name: 'Uncategorized', slug: '' };
}

function toUIProduct(wcProduct: any): UIProduct {
  const cat = getCategoryFromProduct(wcProduct);
  const priceRaw = wcProduct?.price || wcProduct?.regular_price || wcProduct?.regularPrice || '0';
  // Match detail page's social-proof seed so card count is consistent with the detail page.
  const productSlug = wcProduct?.slug || '';
  let hash = 0;
  for (let i = 0; i < productSlug.length; i++) hash = (hash * 31 + productSlug.charCodeAt(i)) | 0;
  const reviewSeed = 12 + (Math.abs(hash) % 18);
  // Distribute default rating across a tasteful pool so the "4 & up" / "5 & up"
  // rating filters have something to filter against. Same slug → same rating.
  const defaultRatingPool = [4.0, 4.3, 4.6, 4.8, 4.9];
  const seededRating = defaultRatingPool[Math.abs(hash) % defaultRatingPool.length];
  const realRating = parseFloat(wcProduct?.average_rating || wcProduct?.averageRating || '0') || 0;
  const realCount = wcProduct?.rating_count || wcProduct?.reviewCount || 0;
  return {
    id: String(wcProduct?.databaseId || wcProduct?.id || wcProduct?.productId || wcProduct?.slug || ''),
    slug: wcProduct?.slug || '',
    name: (wcProduct?.name || '').replace(/^Sweetskyn Body Butter\s*[—-]\s*/i, ''),
    price: parsePrice(priceRaw),
    rating: realCount > 0 ? realRating : seededRating,
    reviews: reviewSeed + realCount,
    category: cat.name,
    categorySlug: cat.slug,
    badge: wcProduct?.featured ? 'Featured' : null,
    image: getImageSrc(wcProduct, FALLBACK_IMAGE),
    alt: getImageAlt(wcProduct),
    href: `/product-detail/${encodeURIComponent(wcProduct?.slug || '')}`,
    onSale: !!(wcProduct?.on_sale || wcProduct?.onSale),
  };
}

const sortOptions = ['Featured', 'Price: Low to High', 'Price: High to Low', 'Top Rated', 'Newest'];

function StarRating({ rating }: {rating: number;}) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) =>
      <svg key={s} className={`w-3 h-3 ${s <= Math.round(rating) ? 'text-accent' : 'text-muted'} fill-current`} viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
    </div>);

}

export default function ProductsClient() {
  const searchParams = useSearchParams();
  const qpCategory = searchParams?.get('category') || '';
  const qpOnSale = searchParams?.get('onSale') === 'true';
  const qpSort = searchParams?.get('sort') || '';
  const qpSearch = (searchParams?.get('search') || '').toLowerCase().trim();

  const [activeCategory, setActiveCategory] = useState('All');
  const [sortBy, setSortBy] = useState(qpSort || 'Featured');
  const [onSaleOnly, setOnSaleOnly] = useState(qpOnSale);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [priceRange, setPriceRange] = useState(5000);
  const [minRating, setMinRating] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;

  // Re-read query params on URL change so nav link clicks flip the filter without a full remount.
  useEffect(() => {
    setOnSaleOnly(qpOnSale);
    if (qpSort) setSortBy(qpSort);
  }, [qpOnSale, qpSort]);

  // Reset to first page whenever any filter changes so pagination stays in sync.
  useEffect(() => {
    setPage(1);
  }, [activeCategory, priceRange, minRating, onSaleOnly, sortBy, qpSearch]);
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const numericId = (id: string) => Math.abs(id.split('').reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0));

  const { data: productsData, loading: productsLoading } = useQuery(GET_PRODUCTS, {
    variables: { first: 50 },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });
  const { data: catsData } = useQuery(GET_PRODUCT_CATEGORIES, {
    variables: { first: 50 },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const allProducts: UIProduct[] = useMemo(() => {
    const nodes = productsData?.products?.nodes || [];
    return transformGraphQLProducts(nodes).map(toUIProduct);
  }, [productsData]);

  const categories = useMemo(() => {
    const cats = catsData?.productCategories?.nodes || [];
    // Hide "Uncategorized" + any category with 0 products from the filter bar.
    const named = cats
      .filter((c: any) => c?.slug && c.slug !== 'uncategorized')
      .map((c: any) => c.name);
    return ['All', ...named];
  }, [catsData]);

  const filtered = allProducts
    .filter((p) => activeCategory === 'All' || p.category === activeCategory)
    .filter((p) => p.price <= priceRange)
    .filter((p) => minRating === 0 || p.rating >= minRating)
    .filter((p) => !onSaleOnly || p.onSale)
    .filter((p) => {
      if (!qpSearch) return true;
      const name = (p.name || '').toLowerCase();
      const category = (p.category || '').toLowerCase();
      return name.includes(qpSearch) || category.includes(qpSearch);
    })
    .sort((a, b) => {
      if (sortBy === 'Price: Low to High') return a.price - b.price;
      if (sortBy === 'Price: High to Low') return b.price - a.price;
      if (sortBy === 'Top Rated') return b.rating - a.rating;
      if (sortBy === 'Newest') {
        // Newer product IDs sort higher — WC auto-increments the post ID.
        return numericId(b.id) - numericId(a.id);
      }
      // Featured: badge='Featured' floats to top, then name A→Z as tiebreaker.
      const aFeatured = a.badge === 'Featured' ? 1 : 0;
      const bFeatured = b.badge === 'Featured' ? 1 : 0;
      if (aFeatured !== bFeatured) return bFeatured - aFeatured;
      return (a.name || '').localeCompare(b.name || '');
    });

  const toggleWishlist = (p: UIProduct) => {
    const pid = numericId(p.id);
    if (isInWishlist(pid)) {
      removeFromWishlist(pid);
    } else {
      addToWishlist({
        id: pid,
        name: p.name,
        price: String(p.price),
        regular_price: String(p.price),
        images: [{ id: 0, src: p.image, name: p.name, alt: p.alt } as any],
        categories: [{ id: 0, name: p.category, slug: p.categorySlug } as any],
        sku: `SSK-BB-${p.id.toUpperCase()}`,
      } as any);
    }
  };

  const handleQuickAdd = (p: UIProduct) => {
    addToCart({
      id: `${p.id}-4oz`,
      product_id: Number(p.id) || 0,
      name: `${p.name} · 4 oz`,
      price: String(p.price),
      regular_price: String(p.price),
      sale_price: '',
      on_sale: false,
      images: [{ id: 0, src: p.image, name: p.name, alt: p.alt } as any],
      categories: [{ id: 0, name: p.category, slug: p.categorySlug } as any],
      sku: `SSK-BB-${p.id.toUpperCase()}-4OZ`,
      meta_data: [
        { key: 'scent', value: p.name },
        { key: 'size', value: '4 oz' },
      ],
    } as any, 1);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
      {/* Page header */}
      <div className="mb-10 pt-4">
        <span className="section-label block mb-2">Our Collection</span>
        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-foreground">
          All Products
        </h1>
      </div>

      <div className="flex gap-10">
        {/* Sidebar — desktop */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-28 space-y-8">
            {/* Category filter */}
            <div>
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground mb-4">Category</h3>
              <div className="space-y-2">
                {categories.map((cat) =>
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`block w-full text-left text-sm py-1.5 transition-colors ${
                  activeCategory === cat ?
                  'text-foreground font-semibold' :
                  'text-muted-foreground hover:text-foreground'}`
                  }>
                  
                    {cat}
                    {activeCategory === cat && <span className="ml-2 inline-block w-4 h-px bg-accent align-middle" />}
                  </button>
                )}
              </div>
            </div>

            {/* Price range */}
            <div>
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground mb-4">
                Price — Up to <span className="font-sans">J$</span>{priceRange.toLocaleString()}
              </h3>
              <input
                type="range"
                min={1000}
                max={5000}
                step={500}
                value={priceRange}
                onChange={(e) => setPriceRange(Number(e.target.value))}
                className="w-full" />

              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span className="font-sans">J$1,000</span>
                <span className="font-sans">J$5,000</span>
              </div>
            </div>

            {/* Rating filter */}
            <div>
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground mb-4">Rating</h3>
              {[5, 4, 3].map((r) =>
              <button
                key={r}
                onClick={() => setMinRating(minRating === r ? 0 : r)}
                className={`w-full flex items-center gap-2 mb-2 cursor-pointer group text-left py-1 px-1 rounded transition-colors ${minRating === r ? 'bg-secondary' : 'hover:bg-secondary/60'}`}>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) =>
                  <svg key={s} className={`w-3 h-3 ${s <= r ? 'text-accent' : 'text-muted'} fill-current`} viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                  )}
                  </div>
                  <span className={`text-[11px] transition-colors ${minRating === r ? 'text-foreground font-semibold' : 'text-muted-foreground group-hover:text-foreground'}`}>& up</span>
                </button>
              )}
              {minRating > 0 && (
                <button
                  onClick={() => setMinRating(0)}
                  className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground underline">
                  Clear rating filter
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest border border-border px-4 py-2 hover:bg-secondary transition-colors"
                onClick={() => setSidebarOpen(!sidebarOpen)}>
                
                <Icon name="AdjustmentsHorizontalIcon" size={16} />
                Filter
              </button>
              <span className="text-sm text-muted-foreground">{filtered.length} products</span>
              {activeCategory !== 'All' &&
              <button
                onClick={() => setActiveCategory('All')}
                className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider bg-secondary px-3 py-1.5 hover:bg-muted transition-colors">

                  {activeCategory} <Icon name="XMarkIcon" size={12} />
                </button>
              }
              {onSaleOnly &&
              <button
                onClick={() => setOnSaleOnly(false)}
                className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider bg-accent text-accent-foreground px-3 py-1.5 hover:bg-accent/90 transition-colors">
                  On Sale <Icon name="XMarkIcon" size={12} />
                </button>
              }
              {qpSearch &&
              <Link
                href="/products"
                className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider bg-primary text-primary-foreground px-3 py-1.5 hover:bg-primary/90 transition-colors">
                  Searching: {qpSearch} <Icon name="XMarkIcon" size={12} />
                </Link>
              }
            </div>

            <div className="flex items-center gap-3">
              <span className="text-[11px] text-muted-foreground hidden sm:block">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-[11px] font-semibold uppercase tracking-wider border border-border bg-background px-3 py-2 focus:outline-none focus:ring-1 focus:ring-ring">
                
                {sortOptions.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {/* Mobile category pills */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-4 mb-4 lg:hidden">
            {categories.map((cat) =>
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`flex-shrink-0 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
              activeCategory === cat ?
              'bg-primary text-primary-foreground' :
              'bg-secondary text-foreground hover:bg-muted'}`
              }>
              
                {cat}
              </button>
            )}
          </div>

          {/* Product grid — paginated */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((product) =>
            <div key={product.id} className="product-card group">
                <Link href={product.href}>
                  <div className="relative overflow-hidden card-rounded bg-secondary aspect-[3/4]">
                    <AppImage
                    src={product.image}
                    alt={product.alt}
                    fill
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    className="object-cover product-card-img" />
                  
                    {product.badge &&
                  <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary text-primary-foreground text-[9px] font-semibold uppercase tracking-wider">
                        {product.badge}
                      </span>
                  }
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/8 transition-all duration-500" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-spring">
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleQuickAdd(product); }}
                        className="w-full bg-white text-primary text-[10px] font-semibold uppercase tracking-widest py-3 hover:bg-accent hover:text-accent-foreground transition-colors">
                        Quick Add
                      </button>
                    </div>
                    <button
                    onClick={(e) => {e.preventDefault();e.stopPropagation();toggleWishlist(product);}}
                    className="absolute top-3 right-3 p-2 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300"
                    aria-label="Toggle wishlist">

                      <Icon
                      name={'HeartIcon'}
                      size={14}
                      variant={isInWishlist(numericId(product.id)) ? 'solid' : 'outline'}
                      className={isInWishlist(numericId(product.id)) ? 'text-accent' : 'text-primary'} />

                    </button>
                  </div>

                  <div className="mt-4 space-y-1.5">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{product.category}</span>
                    <h2 className="font-medium text-sm text-foreground leading-snug">{product.name}</h2>
                    <div className="flex items-center gap-2">
                      <StarRating rating={product.rating} />
                      <span className="text-[10px] text-muted-foreground">({product.reviews})</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground"><span className="font-sans">J$</span>{product.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </Link>
              </div>
            )}
          </div>

          {filtered.length === 0 && !productsLoading &&
          <div className="text-center py-20">
              <p className="font-display text-2xl font-light text-muted-foreground">No products found</p>
              <button onClick={() => {setActiveCategory('All');setPriceRange(5000);}} className="mt-4 text-sm text-accent underline">
                Clear filters
              </button>
            </div>
          }
          {productsLoading && allProducts.length === 0 &&
          <div className="text-center py-20 text-sm text-muted-foreground">Loading products…</div>
          }

          {/* Pagination — only rendered when there's more than one page to show. */}
          {filtered.length > PAGE_SIZE && (() => {
            const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
            return (
              <div className="flex items-center justify-center gap-1.5 mt-12 flex-wrap">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 text-[11px] font-semibold uppercase tracking-widest border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ← Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`min-w-[40px] px-3 py-2 text-[11px] font-semibold border transition-colors ${
                      page === n ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-secondary'
                    }`}>
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-2 text-[11px] font-semibold uppercase tracking-widest border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Next →
                </button>
              </div>
            );
          })()}
        </div>
      </div>
    </div>);

}